import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Server, Copy, RefreshCw, Trash2, Loader2, CheckCircle2, AlertTriangle, Shield
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import scimService from "../../services/scimService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

const ScimProvisioningSection = ({ organizationId }) => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { t } = useTranslation("admin_integrations");

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Fetch SCIM token status
  const { data: scimStatus, isLoading } = useQuery({
    queryKey: ["scim", "token-status", organizationId],
    queryFn: () => scimService.getTokenStatus(organizationId),
    enabled: !!organizationId,
  });

  const hasToken = scimStatus?.has_token;
  const scimUrl = scimStatus?.scim_url;

  // Generate / rotate token
  const generateMutation = useMutation({
    mutationFn: () => scimService.generateToken(organizationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["scim", "token-status", organizationId] });
      setGeneratedToken(data.token);
    },
    onError: (error) => {
      toast.error(formatError(error));
      setShowGenerateModal(false);
    },
  });

  // Revoke token
  const revokeMutation = useMutation({
    mutationFn: () => scimService.revokeToken(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scim", "token-status", organizationId] });
      toast.success(t("scim.toasts.revoked"));
      setShowRevokeModal(false);
    },
    onError: (error) => {
      toast.error(formatError(error));
      setShowRevokeModal(false);
    },
  });

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "url") {
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
      } else {
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      }
    } catch {
      toast.error(t("scim.toasts.copy_failed"));
    }
  };

  const openGenerateModal = () => {
    setGeneratedToken(null);
    setTokenCopied(false);
    setShowGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setGeneratedToken(null);
    setTokenCopied(false);
  };

  return (
    <>
      {/* SCIM Provisioning Card */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <Server className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{t("scim.title")}</h2>
                <p className="text-sm text-slate-600">{t("scim.subtitle")}</p>
              </div>
            </div>
            {hasToken ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {t("scim.status_active")}
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                {t("scim.status_not_configured")}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
            </div>
          ) : hasToken ? (
            <div className="space-y-4">
              {/* SCIM Endpoint URL */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">{t("scim.endpoint_url")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={scimUrl || ""}
                    className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-full text-sm font-mono text-slate-900 select-all"
                  />
                  <button
                    onClick={() => copyToClipboard(scimUrl, "url")}
                    className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-full hover:bg-slate-100 transition-colors text-sm text-slate-700"
                  >
                    {urlCopied ? (
                      <><CheckCircle2 className="w-4 h-4 text-green-600" />{t("common:actions.copied")}</>
                    ) : (
                      <><Copy className="w-4 h-4" />{t("common:actions.copy")}</>
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {t("scim.endpoint_hint")}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={openGenerateModal}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("scim.rotate_token")}
                </button>
                <button
                  onClick={() => setShowRevokeModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-full hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("scim.revoke_token")}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Server className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-4">
                {t("scim.empty_description")}
              </p>
              <button
                onClick={openGenerateModal}
                className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-500 text-white rounded-full hover:from-teal-700 hover:to-cyan-600 transition-all shadow-md font-medium"
              >
                {t("scim.generate_token")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Generate / Rotate Token Modal */}
      <AnimatePresence>
        {showGenerateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4"
            >
              {generatedToken ? (
                // Token generated successfully — show it once
                <div className="p-6 space-y-4">
                  <div className="text-center">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle2 className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{t("scim.generate_modal.generated_title")}</h3>
                    <p className="text-sm text-slate-600 mt-1">{t("scim.generate_modal.generated_description")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("scim.generate_modal.bearer_token")}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={generatedToken}
                        className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-full text-sm font-mono text-slate-900 select-all"
                      />
                      <button
                        onClick={() => copyToClipboard(generatedToken, "token")}
                        className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-full hover:bg-slate-100 transition-colors text-sm text-slate-700"
                      >
                        {tokenCopied ? (
                          <><CheckCircle2 className="w-4 h-4 text-green-600" />{t("common:actions.copied")}</>
                        ) : (
                          <><Copy className="w-4 h-4" />{t("common:actions.copy")}</>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      {t("scim.generate_modal.store_warning")}
                    </p>
                  </div>

                  <button
                    onClick={closeGenerateModal}
                    className="w-full px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-500 text-white rounded-full hover:from-teal-700 hover:to-cyan-600 transition-all shadow-md font-medium"
                  >
                    {t("common:actions.done")}
                  </button>
                </div>
              ) : (
                // Confirmation step
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {hasToken ? t("scim.generate_modal.rotate_title") : t("scim.generate_modal.generate_title")}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {hasToken
                          ? t("scim.generate_modal.rotate_description")
                          : t("scim.generate_modal.generate_description")}
                      </p>
                    </div>
                  </div>

                  {hasToken && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        {t("scim.generate_modal.rotate_warning")}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowGenerateModal(false)}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      onClick={() => generateMutation.mutate()}
                      disabled={generateMutation.isPending}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-500 text-white rounded-full hover:from-teal-700 hover:to-cyan-600 transition-all shadow-md font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {generateMutation.isPending ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />{t("scim.generate_modal.generating")}</>
                      ) : (
                        hasToken ? t("scim.rotate_token") : t("scim.generate_token")
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Revoke Token Confirmation Modal */}
      <AnimatePresence>
        {showRevokeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{t("scim.revoke_modal.title")}</h3>
                  <p className="text-sm text-slate-600">
                    {t("scim.revoke_modal.description")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-2xl">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">
                  {t("scim.revoke_modal.warning")}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowRevokeModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  onClick={() => revokeMutation.mutate()}
                  disabled={revokeMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {revokeMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />{t("scim.revoke_modal.revoking")}</>
                  ) : (
                    t("scim.revoke_modal.confirm")
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ScimProvisioningSection;
